import { MoreVertical } from "lucide-react";
import { useState } from "react";

import { Avatar } from "@/components/avatar";
import { toast } from "@/components/toast/toast-queue";

import type { ChatUser } from "../types";

import { useClickOutside } from "../hooks/useClickOutside";
import {
  type CannotDeleteCommentVariant,
  getCannotDeleteCommentVariant,
  useDeleteChatComment,
} from "../hooks/useDeleteChatComment";
import { CannotDeleteCommentModal } from "./CannotDeleteCommentModal";
import { DeleteConfirmationModal } from "./DeleteConfirmationModal";

interface OthersMessageCardProps {
  canDeleteComment?: boolean;
  children: React.ReactNode;
  commentBatchId?: string;
  isEdited?: boolean;
  rawDate: string;
  sender: ChatUser | undefined;
  timestamp: string;
}

export function OthersMessageCard({
  canDeleteComment = false,
  children,
  commentBatchId,
  isEdited = false,
  rawDate,
  sender,
  timestamp,
}: OthersMessageCardProps) {
  const [_isHovered, setIsHovered] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [cannotDeleteVariant, setCannotDeleteVariant] =
    useState<CannotDeleteCommentVariant | null>(null);

  const deleteComment = useDeleteChatComment();
  const menuRef = useClickOutside(() => setMenuOpen(false));

  const canDelete = canDeleteComment && !!commentBatchId;

  const handleDeleteClick = () => {
    setMenuOpen(false);
    setDeleteModalOpen(true);
  };

  const handleDeleteConfirm = () => {
    if (!commentBatchId) return;

    deleteComment.mutate(commentBatchId, {
      onError: (error) => {
        setDeleteModalOpen(false);

        const variant = getCannotDeleteCommentVariant(error);

        if (variant) {
          setCannotDeleteVariant(variant);
        } else {
          toast.add(
            {
              description: error.message || "Please try again later",
              title: "Error deleting message",
              type: "error",
            },
            {
              timeout: 5000,
            },
          );
        }
      },
      onSuccess: () => {
        setDeleteModalOpen(false);
      },
    });
  };

  const handleDeleteCancel = () => {
    setDeleteModalOpen(false);
    deleteComment.reset();
  };

  const handleCannotDeleteClose = () => {
    setCannotDeleteVariant(null);
    deleteComment.reset();
  };

  return (
    <>
      <div
        className="w-full flex justify-start message-card"
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
      >
        <div className="flex w-full max-w-[368px] w-[368px] min-w-0">
          <Avatar
            alt={sender?.name || ""}
            className="size-8 rounded-full w-[24px] h-[24px] mr-2 shrink-0"
            src={sender?.profilePhotoPath || ""}
          />
          <div className="flex flex-col w-[368px] max-w-[368px] min-w-0">
            <div className="flex items-center justify-between pb-2.5">
              <div className="flex items-center gap-2">
                <span className="text-sm font-medium text-text-primary">
                  {sender?.name || ""}
                </span>
              </div>
              <div className="flex items-center gap-1" title={rawDate}>
                <span className="text-xs text-text-tertiary">{timestamp}</span>
                {isEdited && (
                  <span className="text-xs text-text-tertiary">(Edited)</span>
                )}
                <div className="relative" ref={menuRef}>
                  {canDelete && (
                    <>
                      <button
                        className="w-6 h-6 flex items-center justify-center rounded-full hover:bg-gray-100 cursor-pointer"
                        onClick={(e) => {
                          e.preventDefault();
                          setMenuOpen(!menuOpen);
                        }}
                        type="button"
                      >
                        <MoreVertical className="w-4 h-4 text-text-tertiary" />
                      </button>
                      {menuOpen && (
                        <div className="absolute right-0 mt-1 w-[164px] bg-white rounded-md shadow-lg border border-gray-200 z-10 cursor-pointer">
                          <div className="py-1">
                            <button
                              className="w-full text-left px-4 py-2 text-sm text-text-primary hover:bg-gray-100"
                              onClick={handleDeleteClick}
                              type="button"
                            >
                              Delete comment
                            </button>
                          </div>
                        </div>
                      )}
                    </>
                  )}
                </div>
              </div>
            </div>

            <div className="px-[14px] py-[10px] text-sm others-message-card rounded-bl-sm rounded-tr-sm rounded-br-xl overflow-hidden">
              {children}
            </div>
          </div>
        </div>
      </div>

      {canDelete && (
        <DeleteConfirmationModal
          isLoading={deleteComment.isPending}
          isOpen={deleteModalOpen}
          onCancel={handleDeleteCancel}
          onConfirm={handleDeleteConfirm}
        />
      )}

      <CannotDeleteCommentModal
        isOpen={cannotDeleteVariant !== null}
        onClose={handleCannotDeleteClose}
        variant={cannotDeleteVariant ?? "feedback-blocked"}
      />
    </>
  );
}
